import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { HoverActionsFieldContext, useHoverActionsField } from './context';

export { HoverActionsFieldContext, useHoverActionsField };

const LEAVE_DELAY_MS = 15;

export function HoverActionsOverlayAnchor({
  className,
  children,
  sideOverlay,
  sideOverlayClassName,
  inputClassName,
  style = {},
}) {
  const { registerOverlayAnchor, isOverlayVisible } = useHoverActionsField();

  const setRef = useCallback(
    (node) => {
      registerOverlayAnchor(node);
    },
    [registerOverlayAnchor],
  );

  // Always use the input-group layout so children (e.g. Slate editors) are not
  // remounted when sideOverlay appears after an empty answer gains text.
  const hasSideAction = Boolean(sideOverlay);

  return (
    <div
      className={classnames(
        'hover-actions-input-group',
        'flex',
        'flex-align-items--stretch',
        'flex-1',
        'w-min-none',
        'w-full',
        {
          'hover-actions-input-group--has-side-action': hasSideAction,
        },
        className,
      )}
      style={style}
    >
      <div
        ref={setRef}
        className={classnames(
          'hover-actions-input-group__input',
          'position-relative',
          'flex-1',
          'w-min-none',
          'w-full',
          inputClassName,
        )}
      >
        {children}
      </div>
      {hasSideAction ? (
        <div
          className={classnames(
            'hover-actions-side-overlay',
            sideOverlayClassName,
            {
              'hover-actions-side-overlay--visible':
                isOverlayVisible && sideOverlay,
            },
          )}
        >
          {sideOverlay}
        </div>
      ) : null}
    </div>
  );
}

HoverActionsOverlayAnchor.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node,
  sideOverlay: PropTypes.node,
  sideOverlayClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  inputClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  style: PropTypes.object,
};

function HoverActionsField({
  className,
  children,
  overlay,
  overlayClassName,
  showOverlay = true,
  style = {},
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isInteractionActive, setIsInteractionActive] = useState(false);
  const [overlayAnchor, setOverlayAnchor] = useState(null);
  const leaveTimeoutRef = useRef(null);
  const isMouseInsideRef = useRef(false);

  const isOverlayVisible = showOverlay && (isHovered || isInteractionActive);

  const clearLeaveTimeout = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    isMouseInsideRef.current = true;
    clearLeaveTimeout();
    setIsHovered(true);
  }, [clearLeaveTimeout]);

  const scheduleMouseLeave = useCallback(() => {
    clearLeaveTimeout();
    leaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, LEAVE_DELAY_MS);
  }, [clearLeaveTimeout]);

  const handleMouseLeave = useCallback(() => {
    isMouseInsideRef.current = false;

    if (isInteractionActive) {
      return;
    }

    scheduleMouseLeave();
  }, [isInteractionActive, scheduleMouseLeave]);

  const handleInteractionActiveChange = useCallback(
    (active) => {
      setIsInteractionActive(active);

      if (active) {
        clearLeaveTimeout();
        setIsHovered(true);
        return;
      }

      if (!isMouseInsideRef.current) {
        scheduleMouseLeave();
      }
    },
    [clearLeaveTimeout, scheduleMouseLeave],
  );

  const registerOverlayAnchor = useCallback((node) => {
    setOverlayAnchor(node);
  }, []);

  useEffect(() => () => clearLeaveTimeout(), [clearLeaveTimeout]);

  const contextValue = useMemo(
    () => ({
      onInteractionActiveChange: handleInteractionActiveChange,
      registerOverlayAnchor,
      isOverlayVisible,
    }),
    [handleInteractionActiveChange, registerOverlayAnchor, isOverlayVisible],
  );

  const overlayElement =
    overlay &&
    (overlayAnchor ? (
      createPortal(
        <div className={classnames('hover-actions-overlay', overlayClassName)}>
          {overlay}
        </div>,
        overlayAnchor,
      )
    ) : (
      <div className={classnames('hover-actions-overlay', overlayClassName)}>
        {overlay}
      </div>
    ));

  return (
    <HoverActionsFieldContext.Provider value={contextValue}>
      <div
        className={classnames(
          'hover-actions-field',
          'position-relative',
          className,
          {
            'hover-actions-field--visible': isOverlayVisible,
          },
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={style}
      >
        {children}
        {overlayElement}
      </div>
    </HoverActionsFieldContext.Provider>
  );
}

HoverActionsField.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node,
  overlay: PropTypes.node,
  overlayClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showOverlay: PropTypes.bool,
  style: PropTypes.object,
};

export default HoverActionsField;
